/**
 * FlightBridgeSyncOverlay — shown while the fake "sync" is running (3.5s).
 * Renders as a small floating toast-style card near the ActionIsland.
 */
import { Loader2, Wifi } from 'lucide-react'
import { cn } from '@/lib/utils'

interface FlightBridgeSyncOverlayProps {
  visible: boolean
}

const SYNC_MESSAGES = [
  'Connecting to FlightBridge...',
  'Fetching arrival requests...',
  'Processing inbound data...',
]

export function FlightBridgeSyncOverlay({ visible }: FlightBridgeSyncOverlayProps) {
  if (!visible) return null

  return (
    <div
      className={cn(
        'absolute left-20 top-1/2 -translate-y-1/2 z-40',
        'bg-background/97 backdrop-blur-sm border rounded-xl shadow-xl',
        'px-4 py-3 flex items-center gap-3 min-w-56',
        'animate-in fade-in-0 slide-in-from-left-2 duration-200'
      )}
    >
      <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center shrink-0">
        <Wifi className="w-4 h-4 text-blue-600 dark:text-blue-400" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold">FlightBridge Sync</p>
        <SyncMessageCycle />
      </div>
      <Loader2 className="w-4 h-4 text-blue-500 animate-spin shrink-0" />
    </div>
  )
}

function SyncMessageCycle() {
  // Simple cycling through messages using CSS animation on key change
  const [idx, setIdx] = React.useState(0)

  React.useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % SYNC_MESSAGES.length), 1100)
    return () => clearInterval(t)
  }, [])

  return (
    <p className="text-xs text-muted-foreground truncate">{SYNC_MESSAGES[idx]}</p>
  )
}

// inline React import to avoid issues
import React from 'react'
